import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, Radio, Users } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { ApiError } from '../../../lib/api-client.js';
import { Button } from '../../../shared/components/ui/Button.js';
import { Input } from '../../../shared/components/ui/Input.js';
import { Modal } from '../../../shared/components/ui/Modal.js';
import { serversApi, type Tenant } from '../api.js';

interface Props {
  open: boolean;
  onClose: () => void;
  serverId: number;
  tenant: Tenant;
}

/** Parse do input de limite: '' = sem limite (null). Inválido = undefined. */
function parseLimit(raw: string): number | null | undefined {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  if (!Number.isInteger(n) || n < 0 || n > 1000) return undefined;
  return n;
}

/**
 * Editar tenant: nome + recursos do plano (checkbox) + limites de usuários e
 * canais no estilo "Limites e Conexões". O ChatPlus aplica na hora: chat
 * interno desativado some do painel do cliente (403 na API); limites barram a
 * CRIAÇÃO de usuário/canal novo (o que já existe acima do limite segue
 * funcionando).
 */
export function EditTenantModal({ open, onClose, serverId, tenant }: Props) {
  const [name, setName] = useState(tenant.name);
  const [internalChat, setInternalChat] = useState(tenant.internal_chat_enabled);
  const [voip, setVoip] = useState(tenant.voip_enabled ?? false);
  const [ia, setIa] = useState(tenant.ai_rewrite_enabled ?? false);
  // Strings pros inputs: vazio = sem limite (null no PATCH).
  const [maxUsers, setMaxUsers] = useState(
    tenant.max_users == null ? '' : String(tenant.max_users),
  );
  const [maxChannels, setMaxChannels] = useState(
    tenant.max_channels == null ? '' : String(tenant.max_channels),
  );
  // Teto de mensagens reescritas por mês. Vazio = sem teto (0 no PATCH).
  const [limiteIa, setLimiteIa] = useState(
    tenant.ai_rewrite_monthly_limit ? String(tenant.ai_rewrite_monthly_limit) : '',
  );
  const qc = useQueryClient();

  // Reabrir pra OUTRO tenant re-preenche os campos.
  useEffect(() => {
    if (open) {
      setName(tenant.name);
      setInternalChat(tenant.internal_chat_enabled);
      setVoip(tenant.voip_enabled ?? false);
      setIa(tenant.ai_rewrite_enabled ?? false);
      setLimiteIa(
        tenant.ai_rewrite_monthly_limit ? String(tenant.ai_rewrite_monthly_limit) : '',
      );
      setMaxUsers(tenant.max_users == null ? '' : String(tenant.max_users));
      setMaxChannels(tenant.max_channels == null ? '' : String(tenant.max_channels));
    }
  }, [open, tenant]);

  const usersActive = tenant.users.active;
  const channelsActive = tenant.channels_active ?? 0;

  const parsedUsers = parseLimit(maxUsers);
  const parsedChannels = parseLimit(maxChannels);
  // Vazio vira 0, que o ChatPlus lê como "sem teto".
  const parsedIa = limiteIa.trim() === '' ? 0 : Number.parseInt(limiteIa.trim(), 10);

  // Aviso quando algum limite digitado fica ABAIXO do uso atual.
  const belowUsage: string[] = [];
  if (typeof parsedUsers === 'number' && parsedUsers < usersActive) {
    belowUsage.push(`${usersActive} usuários ativos (limite ${parsedUsers})`);
  }
  if (typeof parsedChannels === 'number' && parsedChannels < channelsActive) {
    belowUsage.push(`${channelsActive} canais ativos (limite ${parsedChannels})`);
  }

  const mut = useMutation({
    mutationFn: async () => {
      if (parsedUsers === undefined || parsedChannels === undefined) {
        throw new Error('Limites devem ser números inteiros entre 0 e 1000 (ou vazio)');
      }
      if (Number.isNaN(parsedIa)) {
        throw new Error('O limite da IA deve ser um número inteiro (ou vazio, pra sem limite)');
      }
      return serversApi.patchTenant(serverId, tenant.id, {
        name: name.trim(),
        internal_chat_enabled: internalChat,
        voip_enabled: voip,
        ai_rewrite_enabled: ia,
        ai_rewrite_monthly_limit: parsedIa,
        max_users: parsedUsers,
        max_channels: parsedChannels,
      });
    },
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['tenants', serverId] });
      toast.success(`Tenant "${data.name}" atualizado`);
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        toast.error(err.problem.title, { description: err.problem.detail });
      } else if (err instanceof Error) {
        toast.error(err.message);
      }
    },
  });

  return (
    <Modal open={open} onClose={onClose} title={`Editar — ${tenant.name}`} width={520}>
      <form
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          if (!name.trim() || mut.isPending) return;
          mut.mutate();
        }}
        className="flex flex-col gap-4"
      >
        <div className="-mt-1 text-[11px] text-n-500">
          tenant <span className="font-mono">#{tenant.id}</span> · slug{' '}
          <span className="font-mono">{tenant.slug}</span>
        </div>

        {/* Faixa "Uso atual" — título em cima, chips embaixo */}
        <div className="flex flex-col gap-2 rounded-md border border-[#FED7AA] bg-[#FFF7ED] px-3 py-2.5">
          <span className="text-[11px] font-bold uppercase tracking-wide text-[#9A3412]">
            Uso atual
          </span>
          <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-md border border-[#FED7AA] bg-white px-2.5 py-1 text-[12px] text-n-700">
            <Radio size={12} strokeWidth={2} />
            Canais ativos <b className="font-mono text-n-900">{channelsActive}</b>
            <span className="font-mono text-n-400">
              [{tenant.max_channels == null ? 'sem limite' : `limite ${tenant.max_channels}`}]
            </span>
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-md border border-[#FED7AA] bg-white px-2.5 py-1 text-[12px] text-n-700">
            <Users size={12} strokeWidth={2} />
            Usuários ativos <b className="font-mono text-n-900">{usersActive}</b>
            <span className="font-mono text-n-400">
              [{tenant.max_users == null ? 'sem limite' : `limite ${tenant.max_users}`}]
            </span>
          </span>
          </div>
        </div>

        <label className="flex flex-col gap-1.5">
          <span className="text-[12px] font-semibold text-n-700">Nome do estabelecimento</span>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Nome do estabelecimento"
            maxLength={120}
            required
          />
        </label>

        {/* Recursos do plano — checkboxes */}
        <div className="overflow-hidden rounded-md border border-n-200">
          <div className="border-b border-n-200 bg-n-50 px-3.5 py-2 text-[11px] font-bold uppercase tracking-wide text-n-500">
            Recursos do plano
          </div>
          <button
            type="button"
            role="checkbox"
            aria-checked={internalChat}
            onClick={() => setInternalChat((v) => !v)}
            className="flex w-full items-start gap-3 px-3.5 py-3 text-left hover:bg-n-50"
          >
            <span
              className={`mt-px flex h-[20px] w-[20px] flex-shrink-0 items-center justify-center rounded-[5px] border-2 ${
                internalChat ? 'border-brand-500 bg-brand-500' : 'border-n-300 bg-white'
              }`}
              aria-hidden="true"
            >
              {internalChat && <Check size={14} strokeWidth={3.5} className="text-white" />}
            </span>
            <span className="flex-1">
              <span className="flex flex-wrap items-center gap-2 text-[13px] font-semibold text-n-900">
                Habilitar chat interno
                <span
                  className={
                    internalChat
                      ? 'rounded-pill bg-success-50 px-2 py-0.5 text-[10px] font-bold text-success-700'
                      : 'rounded-pill bg-n-100 px-2 py-0.5 text-[10px] font-bold text-n-500'
                  }
                >
                  {internalChat ? 'Ativo' : 'Desativado'}
                </span>
              </span>
              <span className="mt-0.5 block text-[11px] text-n-500">
                Conversa entre os colaboradores da empresa — ícone de chat na topbar do painel
              </span>
            </span>
          </button>
          <button
            type="button"
            role="checkbox"
            aria-checked={voip}
            onClick={() => setVoip((v) => !v)}
            className="flex w-full items-start gap-3 px-3.5 py-3 text-left hover:bg-n-50"
          >
            <span
              className={`mt-px flex h-[20px] w-[20px] flex-shrink-0 items-center justify-center rounded-[5px] border-2 ${
                voip ? 'border-brand-500 bg-brand-500' : 'border-n-300 bg-white'
              }`}
              aria-hidden="true"
            >
              {voip && <Check size={14} strokeWidth={3.5} className="text-white" />}
            </span>
            <span className="flex-1">
              <span className="flex flex-wrap items-center gap-2 text-[13px] font-semibold text-n-900">
                Habilitar chamadas de voz (VoIP)
                <span
                  className={
                    voip
                      ? 'rounded-pill bg-success-50 px-2 py-0.5 text-[10px] font-bold text-success-700'
                      : 'rounded-pill bg-n-100 px-2 py-0.5 text-[10px] font-bold text-n-500'
                  }
                >
                  {voip ? 'Ativo' : 'Desativado'}
                </span>
              </span>
              <span className="mt-0.5 block text-[11px] text-n-500">
                Atendente liga pro cliente pelo painel — apenas canais Qr Code API V2
              </span>
            </span>
          </button>
          <button
            type="button"
            role="checkbox"
            aria-checked={ia}
            onClick={() => setIa((v) => !v)}
            className="flex w-full items-start gap-3 px-3.5 py-3 text-left hover:bg-n-50"
          >
            <span
              className={`mt-px flex h-[20px] w-[20px] flex-shrink-0 items-center justify-center rounded-[5px] border-2 ${
                ia ? 'border-brand-500 bg-brand-500' : 'border-n-300 bg-white'
              }`}
              aria-hidden="true"
            >
              {ia && <Check size={14} strokeWidth={3.5} className="text-white" />}
            </span>
            <span className="flex-1">
              <span className="flex flex-wrap items-center gap-2 text-[13px] font-semibold text-n-900">
                Melhoria por IA
                <span
                  className={
                    ia
                      ? 'rounded-pill bg-success-50 px-2 py-0.5 text-[10px] font-bold text-success-700'
                      : 'rounded-pill bg-n-100 px-2 py-0.5 text-[10px] font-bold text-n-500'
                  }
                >
                  {ia ? 'Ativo' : 'Desativado'}
                </span>
              </span>
              <span className="mt-0.5 block text-[11px] text-n-500">
                A IA reescreve as mensagens agrupadas antes de saírem — a conta é nossa.
                O cliente escolhe em quais canais oficiais usar.
              </span>
            </span>
          </button>
        </div>

        {/* Limites e Conexões — caixas com label dentro */}
        <div>
          <div className="mb-2 text-[13px] font-semibold text-n-700">Limites e Conexões</div>
          <div className="grid grid-cols-2 gap-3">
            <label className="cursor-text rounded-md border border-n-200 px-3.5 pb-2 pt-2 transition-colors focus-within:border-brand-500 focus-within:ring-[3px] focus-within:ring-brand-500/15">
              <span className="block text-[11.5px] text-n-500">Limite de Usuários</span>
              <input
                value={maxUsers}
                onChange={(e) => setMaxUsers(e.target.value.replace(/\D/g, ''))}
                placeholder="Vazio = sem limite"
                inputMode="numeric"
                className="mt-0.5 w-full border-none bg-transparent p-0 font-sans text-[17px] font-semibold text-n-900 outline-none placeholder:text-[13px] placeholder:font-normal placeholder:text-n-300"
              />
            </label>
            <label className="cursor-text rounded-md border border-n-200 px-3.5 pb-2 pt-2 transition-colors focus-within:border-brand-500 focus-within:ring-[3px] focus-within:ring-brand-500/15">
              <span className="block text-[11.5px] text-n-500">Limite de Canais</span>
              <input
                value={maxChannels}
                onChange={(e) => setMaxChannels(e.target.value.replace(/\D/g, ''))}
                placeholder="Vazio = sem limite"
                inputMode="numeric"
                className="mt-0.5 w-full border-none bg-transparent p-0 font-sans text-[17px] font-semibold text-n-900 outline-none placeholder:text-[13px] placeholder:font-normal placeholder:text-n-300"
              />
            </label>
          </div>
          <div className="mt-1.5 text-[11px] text-n-500">
            Conforme o plano contratado. Vale pra <b>criar novos</b> — o que já existe acima do
            limite continua funcionando. Vazio = sem limite.
          </div>
          {ia && (
            <div className="mt-3">
              <label className="block cursor-text rounded-md border border-n-200 px-3.5 pb-2 pt-2 transition-colors focus-within:border-brand-500 focus-within:ring-[3px] focus-within:ring-brand-500/15">
                <span className="block text-[11.5px] text-n-500">
                  Mensagens melhoradas por IA / mês
                </span>
                <input
                  value={limiteIa}
                  onChange={(e) => setLimiteIa(e.target.value.replace(/\D/g, ''))}
                  placeholder="Vazio = sem limite"
                  inputMode="numeric"
                  className="mt-0.5 w-full border-none bg-transparent p-0 font-sans text-[17px] font-semibold text-n-900 outline-none placeholder:text-[13px] placeholder:font-normal placeholder:text-n-300"
                />
              </label>
              <div className="mt-1.5 text-[11px] text-n-500">
                Freio contra anomalia, não racionamento — o custo real é de poucos reais por
                mês. Estourou, as mensagens saem como o atendente digitou.
              </div>
            </div>
          )}
        </div>

        {/* Aviso: limite abaixo do uso atual */}
        {belowUsage.length > 0 && (
          <div className="flex gap-2 rounded-md border border-warning-100 bg-warning-50 px-3 py-2 text-[11.5px] text-warning-700">
            <span aria-hidden="true">⚠️</span>
            <span>
              Limite abaixo do uso atual: {belowUsage.join(' e ')}. Os existentes continuam
              funcionando, mas nada novo poderá ser criado até liberar espaço.
            </span>
          </div>
        )}

        <div className="mt-1 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" loading={mut.isPending} disabled={!name.trim()}>
            Salvar alterações
          </Button>
        </div>
      </form>
    </Modal>
  );
}
